import {useEffect, useState} from 'react';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import {FeaturesEnum} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {useAccountInformation} from 'presentation/pages/private/hooks/useAccountInformation';
import {useElectronicBillSubscription} from 'presentation/pages/private/hooks/useElectronicBillSubscription';
import {useGetAdherenceDigitalPaymentQuery} from 'presentation/pages/private/home/query/useGetAdherenceDigitalPaymentQuery';
import {patchAdherenceDigitalPayment} from 'presentation/pages/private/home/services/patchAdherenceDigitalPayment';
import {useQueryClient} from '@tanstack/react-query';

const ONE_HOUR_MS = 60 * 60 * 1000;
const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000;

export function useHomeElectronicBillDrawer() {
	const {isEnabled: isFeatureEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_ELETRONIC_BILL_ALERT,
	);
	const queryClient = useQueryClient();

	const {accesses} = useAccountDataStore(state => state.accountData);
	const {handleToggleSubscriptionModal} = useElectronicBillSubscription();
	const {isIndividualPlan} = useAccountInformation();
	const isSubscribed = accesses?.accessElectronicBill ?? false;
	const shouldFetchAdherenceDate = isFeatureEnabled && !isSubscribed && isIndividualPlan;

	const {data: adherenceData, isSuccess: isAdherenceSuccess} =
		useGetAdherenceDigitalPaymentQuery(shouldFetchAdherenceDate);

	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [isDontShowChecked, setIsDontShowChecked] = useState(false);
	const [isSeeLaterLoading, setIsSeeLaterLoading] = useState(false);
	const [isPatchError, setIsPatchError] = useState(false);

	useEffect(() => {
		if (!shouldFetchAdherenceDate) {
			setIsDrawerOpen(false);
			return;
		}

		if (!isAdherenceSuccess) {
			setIsDrawerOpen(false);
			return;
		}

		const adherencePaymentDate = adherenceData?.adherencePaymentDate;
		const shouldShowByDate =
			typeof adherencePaymentDate !== 'number' || adherencePaymentDate < Date.now();

		setIsDrawerOpen(shouldShowByDate);
	}, [adherenceData?.adherencePaymentDate, isAdherenceSuccess, shouldFetchAdherenceDate]);

	async function handlePersistAdherenceDate(isSeeLater = false) {
		if (isSeeLater) setIsSeeLaterLoading(true);

		const adherencePaymentDate =
			Date.now() + (isDontShowChecked ? NINETY_DAYS_MS : ONE_HOUR_MS);

		const isPatched = await patchAdherenceDigitalPayment({adherencePaymentDate})
			.then(() => true)
			.catch(() => false);

		if (isSeeLater) setIsSeeLaterLoading(false);

		if (!isPatched) {
			setIsPatchError(true);
			return;
		}

		queryClient.setQueryData([QUERY_KEYS.ADHERENCE_DIGITAL_PAYMENT], {
			adherencePaymentDate,
		});

		setIsDrawerOpen(false);
	}

	function handleClose() {
		void handlePersistAdherenceDate(false);
	}

	async function handleSeeLater() {
		await handlePersistAdherenceDate(true);
	}

	function handleCloseError() {
		setIsPatchError(false);
	}

	function handleSubscribe() {
		setIsDrawerOpen(false);
		handleToggleSubscriptionModal(true);
	}

	function toggleDontShow() {
		setIsDontShowChecked(prev => !prev);
	}

	return {
		isDrawerOpen,
		isDontShowChecked,
		isSeeLaterLoading,
		isPatchError,
		handleClose,
		handleSeeLater,
		handleSubscribe,
		handleCloseError,
		toggleDontShow,
	};
}
